import React from 'react';
import { X } from 'lucide-react';
import Button from '../button';
import { formatToNaira } from 'functions/currencyConveter';

interface DetailItem {
  label: string;
  value: string;
  isPercentage?: boolean;
  isInterest?: boolean;
  isSuccess?: boolean;
}

interface SingleGoalData {
  goalName: string;
  targetAmount: number;
  preferedSavingAmount: number;
  startDate: string;
  endDate: string;
  nextSavingDate: string;
  status: string;
  duration: number;
  savingsFrequency: string;
  savingType: string;
  interest: number;
  savingMethod: string;
  autoWithDraw: boolean;
  autoSave?: boolean;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  amount: string;
  status: string;
  details: DetailItem[];
  singleGoal?: SingleGoalData | null;
  primaryAction: { text: string; onClick: () => void };
  secondaryAction?: { text: string; onClick: () => void };
}

const SavingsTabModal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  amount,
  status,
  details,
  singleGoal,
  primaryAction,
  secondaryAction,
}) => {
  if (!isOpen) return null;
  const isRolledOver = status === 'RolledOver';

  const modalDetails: DetailItem[] = [
    { label: 'Savings Title', value: title },
    { label: 'Savings Goal', value: amount },
    ...details,
  ];

  // Add single goal data if available
  if (singleGoal) {
    modalDetails.push(
      { label: 'Savings Frequency', value: singleGoal.savingsFrequency },
      { label: 'Saving Type', value: singleGoal.savingType },
      { label: 'Next Saving Date', value: singleGoal.nextSavingDate },
      {
        label: 'Preferred Saving Amount',
        value: formatToNaira(singleGoal.preferedSavingAmount),
      },
      { label: 'Saving Method', value: singleGoal.savingMethod },
      { label: 'Auto Withdraw', value: singleGoal.autoWithDraw ? 'Yes' : 'No' },
      { label: 'Auto Save', value: `${singleGoal.autoSave ? 'Yes' : 'No'}` },
      { label: 'Interest', value: `${formatToNaira(singleGoal.interest)}` }
    );
  }

  return (
    <div className="flex overflow-y-auto fixed inset-0 z-[9999] justify-center items-center px-5 w-full  bg-gray-500 bg-opacity-50">
      <div className="relative h-[90%] overflow-auto p-6 w-full max-w-md bg-white rounded-lg shadow-lg">
        <button
          type="button"
          className="absolute top-3 right-3 text-gray-400 hover:text-gray-900"
          onClick={onClose}
        >
          <X size={20} />
        </button>

        <h2 className="text-lg font-semibold text-gray-800">
          {isRolledOver ? 'Rolled-over Savings' : 'Ongoing Savings'}
        </h2>
        <div className="p-4 mt-4 text-center bg-gray-50 rounded-md">
          <p className="text-3xl font-bold text-gray-900">{amount}</p>
          <span
            className={`inline-block px-3 py-1 mt-2 text-sm rounded-lg ${
              isRolledOver || status === 'Active' || status === 'Completed'
                ? 'text-green-600 bg-green-100'
                : 'text-red-600 bg-red-100'
            }`}
          >
            {isRolledOver ? 'Successful' : status}
          </span>
        </div>

        <div className="mt-6 space-y-3">
          {modalDetails.map((item, index) => (
            <div
              key={`${item.label}-${index}`}
              className="flex justify-between items-center text-gray-700"
            >
              <span>{item.label}</span>
              <span
                className={`font-medium capitalize ${
                  item.isSuccess
                    ? 'text-green-600'
                    : item.isPercentage
                      ? 'text-red-600'
                      : ''
                }`}
              >
                {item.value}
              </span>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-4 mt-6">
          {status !== 'Completed' ? (
            <>
              <Button
                onClick={secondaryAction?.onClick}
                className="w-full text-yellow-500 bg-white border border-yellow-500 hover:bg-yellow-50"
              >
                {isRolledOver ? 'Withdraw' : 'Cancel'}
              </Button>
              <Button
                onClick={primaryAction.onClick}
                className="w-full text-white bg-yellow-500 hover:bg-yellow-600"
              >
                {isRolledOver ? 'Reinvest' : 'Withdraw'}
              </Button>
            </>
          ) : (
            <Button
              onClick={onClose}
              className="col-span-2 w-full text-white bg-yellow-500 hover:bg-yellow-600"
            >
              Close
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SavingsTabModal;
